import { useEffect } from 'react'

import { $generateNodesFromDOM } from '@lexical/html'
import { mergeRegister } from '@lexical/utils'
import { $insertNodes, COMMAND_PRIORITY_EDITOR, COMMAND_PRIORITY_LOW, PASTE_COMMAND } from 'lexical'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'

import { $createImageNode, INSERT_IMAGE_COMMAND } from './ImageNode'

type InlineImagePluginProps = {
  onPasteImage: (file: File) => Promise<string>
  onUploadError: (message: string) => void
}

const EXTERNAL_IMAGE_PASTE_MESSAGE = 'Pasted image could not be imported because it is hosted by an External Service.\nCopy the image itself, take a screenshot, or download/upload the image manually.'

function isExternalImageSource(source: string) {
  if (!/^https?:\/\//i.test(source)) {
    return false
  }

  try {
    return new URL(source).origin !== window.location.origin
  } catch {
    return true
  }
}

function removeExternalImagesFromHtml(html: string) {
  const parsedDocument = new DOMParser().parseFromString(html, 'text/html')
  const externalImages = Array.from(parsedDocument.querySelectorAll<HTMLImageElement>('img[src]'))
    .filter((image) => isExternalImageSource(image.getAttribute('src') || ''))

  externalImages.forEach((image) => image.remove())

  return {
    document: parsedDocument,
    removedImages: externalImages.length,
  }
}

export function InlineImagePlugin({ onPasteImage, onUploadError }: InlineImagePluginProps) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          editor.update(() => {
            $insertNodes([$createImageNode(payload)])
          })
          return true
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          const clipboardEvent = event as ClipboardEvent | null
          const html = clipboardEvent?.clipboardData?.getData('text/html') || ''
          const imageItem = clipboardEvent?.clipboardData?.items
            ? Array.from(clipboardEvent.clipboardData.items).find((item) => item.type.startsWith('image/'))
            : null

          const file = imageItem?.getAsFile()
          if (file) {
            clipboardEvent?.preventDefault()
            void onPasteImage(file)
              .then((src) => {
                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                  altText: file.name || 'Pasted image',
                  src,
                })
              })
              .catch(() => {
                onUploadError('Unable to paste image right now')
              })

            return true
          }

          if (html && /<img\b/i.test(html)) {
            const { document, removedImages } = removeExternalImagesFromHtml(html)

            if (removedImages > 0) {
              clipboardEvent?.preventDefault()
              onUploadError(EXTERNAL_IMAGE_PASTE_MESSAGE)

              editor.update(() => {
                const nodes = $generateNodesFromDOM(editor, document)
                if (nodes.length) {
                  $insertNodes(nodes)
                }
              })

              return true
            }
          }

          return false
        },
        COMMAND_PRIORITY_LOW,
      ),
    )
  }, [editor, onPasteImage, onUploadError])

  return null
}
